import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { register as registerUser } from '../../apis/auth';
import { setSession } from '../../store/reducers/authReducer';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useShowNotification } from '../../hooks/useShowNotification';
import { useExceptionHandler } from '../../hooks/useExceptionHandler';
import { FormErrors } from '../../components/common/form/FormErrors';

export const RegisterPage = () => {
    useDocumentTitle('Register');

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const { handleException } = useExceptionHandler();
    const showNotification = useShowNotification('Register', 'sign-out');

    const {
        register,
        formState: { errors },
        getValues,
        handleSubmit
    } = useForm();

    const onSubmit = async ({ name, email, password, passwordConfirmation }) => {
        try {
            if (isLoading === 'loading') return;

            setIsLoading(true);
            const response = await registerUser({ name, email, password, passwordConfirmation });
            setIsLoading(false);

            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setSession(response));
                navigate('/');
                return;
            }

            if (response.exception) {
                handleException(response.exception);
                return;
            }

            showNotification(response.message, 'warning');
        } catch (error) {
            setIsLoading(false);
            showNotification('Oops something went wrong.', 'danger');
        }
    };

    const nameValidations = {
        required: 'Name is required.'
    };

    const emailValidations = {
        required: 'Email is required.',
        pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
            message: 'Invalid email address.'
        }
    };

    const passwordValidations = {
        required: 'Password is required.',
        minLength: {
            value: 6,
            message: 'Password must be at least 6 characters long.'
        }
    };

    const passwordConfirmationValidations = {
        required: 'Password confirmation is required.',
        validate: {
            confirmPassword: (value) =>
                value === getValues('password') || 'Password and password confirmation must match.'
        }
    };

    return (
        <form className="register-form" onSubmit={handleSubmit(onSubmit)}>
            <h1 className="h3 fw-normal text-dark my-3">Register</h1>
            <FormErrors errors={errors} />
            <div className="form-floating">
                <input
                    {...register('name', nameValidations)}
                    type="text"
                    className="form-control"
                    id="nameInput"
                    placeholder="Name"
                />
                <label htmlFor="nameInput">Name</label>
            </div>
            <div className="form-floating">
                <input
                    {...register('email', emailValidations)}
                    type="email"
                    className="form-control"
                    id="emailInput"
                    placeholder="name@example.com"
                />
                <label htmlFor="emailInput">Email address</label>
            </div>
            <div className="form-floating">
                <input
                    {...register('password', passwordValidations)}
                    type="password"
                    className="form-control"
                    id="passwordInput"
                    placeholder="Password"
                />
                <label htmlFor="passwordInput">Password</label>
            </div>
            <div className="form-floating">
                <input
                    {...register('passwordConfirmation', passwordConfirmationValidations)}
                    type="password"
                    className="form-control"
                    id="passwordConfirmationInput"
                    placeholder="Password confirmation"
                />
                <label htmlFor="passwordConfirmationInput">Password confirmation</label>
            </div>
            <button className="w-100 btn btn-lg btn-primary my-3" type="submit">
                Continue
            </button>
            <p className="mt-3 mb-0">Already have an account?</p>
            <Link to="/login" className="link-primary text-decoration-none mb-3">
                Login
            </Link>
        </form>
    );
};
